import { useState, useCallback } from 'react';
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  getDoc,
  doc,
} from 'firebase/firestore';
import { SAFEGUARD_REASONS } from './useInboxData';
import {
  buildResendPreset,
  pickBulkResendDrafts,
  resendBlockReason,
} from '../utils/safeGuardResend';

const MAX_BULK_DRAFTS = 300;

const BLOCK_MESSAGES = {
  blocked_reason: 'This mail was blocked for a reason SafeGuard cannot lift (CRM or invalid address).',
  no_contact: 'This mail is not linked to a contact, so it cannot be opened in a send mode.',
  no_recipient: 'This mail has no recipient address.',
  no_content: 'This mail has no content to send.',
  missing: 'Mail not found.',
};

/**
 * useSafeGuardResend — turns parked SafeGuard drafts into what the email
 * modes need: the contact row plus the mail as it was meant to go out.
 */
export default function useSafeGuardResend(userId) {
  const db = getFirestore();
  const [loading, setLoading] = useState(false);

  const loadContact = useCallback(async (leadId) => {
    try {
      const snap = await getDoc(doc(db, `World/${userId}/db/${leadId}`));
      return snap.exists() ? { id: snap.id, ...snap.data() } : null;
    } catch (err) {
      console.warn('[SafeGuardResend] contact load failed:', leadId, err?.message);
      return null;
    }
  }, [db, userId]);

  const loadPrepared = useCallback(async (contact) => {
    if (!contact || contact.has_prepared_email !== true) return null;
    try {
      const snap = await getDoc(doc(db, `World/${userId}/preparedEmails/${contact.id}`));
      return snap.exists() ? snap.data() : null;
    } catch (_) {
      return null;
    }
  }, [db, userId]);

  /** @returns {Promise<{ rowData?: object, error?: string }>} */
  const prepareSingle = useCallback(async (draft) => {
    const blocked = resendBlockReason(draft);
    if (blocked) return { error: BLOCK_MESSAGES[blocked] };
    setLoading(true);
    try {
      const contact = await loadContact(draft.leadId);
      if (!contact) return { error: 'The contact of this mail no longer exists.' };
      const prepared = await loadPrepared(contact);
      return { rowData: { ...contact, resendDraft: buildResendPreset(draft, prepared) } };
    } finally {
      setLoading(false);
    }
  }, [loadContact, loadPrepared]);

  /**
   * @returns {Promise<{ contacts?: object[], presetEmails?: object, skipped?: number, error?: string }>}
   */
  const prepareAll = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(
        collection(db, 'world_drafts', userId, 'emails'),
        where('status', '==', 'draft'),
        where('reason', 'in', SAFEGUARD_REASONS),
        orderBy('timestamp', 'desc'),
        limit(MAX_BULK_DRAFTS),
      ));
      const drafts = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const picked = pickBulkResendDrafts(drafts);
      const rows = await Promise.all(picked.map(async (draft) => {
        const contact = await loadContact(draft.leadId);
        if (!contact) return null;
        const prepared = await loadPrepared(contact);
        return { contact, preset: buildResendPreset(draft, prepared) };
      }));
      const ready = rows.filter(Boolean);
      if (ready.length === 0) {
        return { error: 'No SafeGuard mail can be re-sent (missing contact, recipient or content).' };
      }
      return {
        contacts: ready.map((r) => r.contact),
        presetEmails: Object.fromEntries(ready.map((r) => [r.contact.id, r.preset])),
        skipped: drafts.length - ready.length,
      };
    } catch (err) {
      console.error('[SafeGuardResend] bulk load failed:', err);
      return { error: 'Could not load the SafeGuard mails.' };
    } finally {
      setLoading(false);
    }
  }, [db, userId, loadContact, loadPrepared]);

  return { loading, prepareSingle, prepareAll };
}
